import React, { FC, useState } from "react";

import { EditorState, SerializedEditorState } from "lexical";

import { useEditorState } from "~/hooks";

import { LexicalEditor } from "~/components/atoms";
import { TextEditor } from "~/components/organisms";

import { SignInWrapper } from "~/ui";

export const LexicalEditorTestIndex: FC = () => {
  const [draftEditorState] = useEditorState();
  const [lexicalEditorState, setLexicalEditorState] =
    useState<SerializedEditorState | null>(null);
  const [lexicalPlainText, setLexicalPlainText] = useState("");
  const [isDraftDebugOpen, setIsDraftDebugOpen] = useState(false);
  const [isLexicalDebugOpen, setIsLexicalDebugOpen] = useState(false);

  const handleLexicalChange = (editorState: EditorState) => {
    // エディタ状態をJSON形式に変換
    const json = editorState.toJSON();
    setLexicalEditorState(json);

    // プレーンテキストを抽出
    editorState.read(() => {
      const plainText = editorState.read(() => {
        const root = editorState._nodeMap.get("root");
        if (root) {
          return root.getTextContent();
        }
        return "";
      });
      setLexicalPlainText(plainText);
    });
  };

  return (
    <SignInWrapper>
      <div className="gap-y-4 mx-6 px-4 pb-12 pt-11 miniTablet:px-14 max-w-7xl w-full bg-white border-secondary-400 border-solid border rounded-lg shadow">
        <h1 className="text-3xl font-bold mb-6">エディタ比較テスト</h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Draft.js Editor */}
          <div className="space-y-4">
            <div>
              <h2 className="text-2xl font-semibold mb-2 text-primary-600">
                Draft.js エディタ（既存）
              </h2>
              <p className="text-gray-600 text-sm mb-4">
                現在使用中のDraft.jsベースのエディタ
              </p>
            </div>

            <div className="border border-gray-300 rounded p-4 bg-gray-50">
              <TextEditor
                readOnly={false}
                mentions={[]}
                editorState={draftEditorState.value}
                setEditorState={draftEditorState.onChange}
                files={[]}
                onChangeFiles={() => {}}
                placeholder="Draft.js エディタでテキストを入力..."
                required
                defaultAutoFucus={false}
              />
            </div>

            <div className="space-y-2 text-sm">
              <h3 className="font-semibold">機能テスト:</h3>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>テキストを選択してツールバーを表示</li>
                <li>太字ボタン（B）をクリック</li>
                <li>下線ボタン（U）をクリック</li>
                <li>見出し1ボタン（H1）をクリック</li>
              </ul>
            </div>

            <div className="space-y-2 text-sm bg-blue-50 p-3 rounded">
              <button
                type="button"
                onClick={() => setIsDraftDebugOpen(!isDraftDebugOpen)}
                className="w-full flex items-center justify-between font-semibold text-blue-800 hover:text-blue-900"
              >
                <span>デバッグ情報</span>
                <span className="text-lg">{isDraftDebugOpen ? "▼" : "▶"}</span>
              </button>
              {isDraftDebugOpen && (
                <div className="mt-2 space-y-2">
                  <div>
                    <strong>hasText:</strong>{" "}
                    {draftEditorState.hasText ? "Yes" : "No"}
                  </div>
                  <div>
                    <strong>plainText:</strong>
                    <pre className="bg-white p-2 rounded mt-1 text-xs overflow-auto max-h-20">
                      {draftEditorState.plainText}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Lexical Editor */}
          <div className="space-y-4">
            <div>
              <h2 className="text-2xl font-semibold mb-2 text-green-600">
                Lexical エディタ（新規）
              </h2>
              <p className="text-gray-600 text-sm mb-4">
                新しく作成したLexicalベースのエディタ
              </p>
            </div>

            <div className="border border-gray-300 rounded p-4 bg-gray-50">
              <LexicalEditor
                readOnly={false}
                placeholder="Lexical エディタでテキストを入力..."
                className="min-h-[200px]"
                onChange={handleLexicalChange}
              />
            </div>

            <div className="space-y-2 text-sm">
              <h3 className="font-semibold">機能テスト:</h3>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>テキストを選択してツールバーを表示</li>
                <li>太字ボタン（B）をクリック</li>
                <li>下線ボタン（U）をクリック</li>
                <li>見出し1ボタン（H1）をクリック</li>
                <li>URLを入力（例: https://example.com）してリンク自動検出</li>
              </ul>
            </div>

            <div className="space-y-2 text-sm bg-green-50 p-3 rounded">
              <h3 className="font-semibold text-green-800">実装状況:</h3>
              <ul className="list-disc list-inside space-y-1">
                <li>✅ 基本エディタ</li>
                <li>✅ テキストフォーマット（太字、下線、見出し1）</li>
                <li>✅ インラインツールバー</li>
                <li>✅ Undo/Redo（Ctrl+Z / Ctrl+Y）</li>
                <li>✅ リンク自動検出（URL/Email）</li>
                <li>✅ State管理（useState）</li>
                <li>⏳ メンション機能（未実装）</li>
                <li>⏳ 画像埋め込み（未実装）</li>
              </ul>
            </div>

            <div className="space-y-2 text-sm bg-green-50 p-3 rounded">
              <button
                type="button"
                onClick={() => setIsLexicalDebugOpen(!isLexicalDebugOpen)}
                className="w-full flex items-center justify-between font-semibold text-green-800 hover:text-green-900"
              >
                <span>デバッグ情報</span>
                <span className="text-lg">
                  {isLexicalDebugOpen ? "▼" : "▶"}
                </span>
              </button>
              {isLexicalDebugOpen && (
                <div className="mt-2 space-y-2">
                  <div>
                    <strong>hasText:</strong>{" "}
                    {lexicalPlainText.length > 0 ? "Yes" : "No"}
                  </div>
                  <div>
                    <strong>plainText:</strong>
                    <pre className="bg-white p-2 rounded mt-1 text-xs overflow-auto max-h-20">
                      {lexicalPlainText || "(空)"}
                    </pre>
                  </div>
                  <div>
                    <strong>jsonContent:</strong>
                    <pre className="bg-white p-2 rounded mt-1 text-xs overflow-auto max-h-40">
                      {lexicalEditorState
                        ? JSON.stringify(lexicalEditorState, null, 2)
                        : "(空)"}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </SignInWrapper>
  );
};
